import { useCallback, useEffect, useRef } from 'react';
import useLogout from './useLogout';

export const ADMIN_LAST_ACTIVITY_KEY = 'usfcr_admin_last_activity_at';
export const ADMIN_LOGOUT_EVENT_KEY = 'usfcr_admin_inactivity_logout_event';
export const ADMIN_LOGOUT_REASON_KEY = 'usfcr_admin_logout_reason';
export const INACTIVITY_LOGOUT_REASON = 'inactivity';
const HARD_REDIRECT_FALLBACK_MS = 1500;
const MIN_MOUSEMOVE_DELTA_PX = 10;
const TIMEOUT_WATCHDOG_INTERVAL_MS = 15000;
const SIGN_IN_PATH = '/sign-in';

export default function useInactivityLogout(timeout = 30 * 60 * 1000) {
  const logout = useLogout();
  const timerIdRef = useRef(null);
  const intervalIdRef = useRef(null);
  const hasLoggedOutRef = useRef(false);
  const lastMousePositionRef = useRef({ x: null, y: null });
  const isOnSignInPage = useCallback(() => window.location.pathname === SIGN_IN_PATH, []);

  const getLastActivity = useCallback(() => {
    const value = Number(localStorage.getItem(ADMIN_LAST_ACTIVITY_KEY));
    return Number.isFinite(value) && value > 0 ? value : 0;
  }, []);

  const markActivity = useCallback(() => {
    localStorage.setItem(ADMIN_LAST_ACTIVITY_KEY, String(Date.now()));
  }, []);

  const isExpired = useCallback(() => {
    const lastActivity = getLastActivity();
    if (!lastActivity) {
      return false;
    }
    return Date.now() - lastActivity >= timeout;
  }, [getLastActivity, timeout]);

  const performLogout = useCallback(() => {
    if (isOnSignInPage()) {
      hasLoggedOutRef.current = false;
      markActivity();
      return;
    }
    if (hasLoggedOutRef.current) {
      return;
    }
    hasLoggedOutRef.current = true;
    sessionStorage.setItem(ADMIN_LOGOUT_REASON_KEY, INACTIVITY_LOGOUT_REASON);
    localStorage.setItem(ADMIN_LOGOUT_EVENT_KEY, String(Date.now()));
    logout();
    window.setTimeout(() => {
      if (window.location.pathname !== '/sign-in') {
        window.location.assign('/sign-in');
      }
    }, HARD_REDIRECT_FALLBACK_MS);
  }, [isOnSignInPage, logout, markActivity]);

  const resetTimer = useCallback(() => {
    if (timerIdRef.current) {
      clearTimeout(timerIdRef.current);
    }
    timerIdRef.current = setTimeout(() => {
      if (isExpired()) {
        performLogout();
      } else {
        resetTimer();
      }
    }, timeout);
  }, [isExpired, performLogout, timeout]);

  const handleActivity = useCallback((event) => {
    if (hasLoggedOutRef.current) {
      return;
    }
    if (isOnSignInPage()) {
      hasLoggedOutRef.current = false;
      markActivity();
      resetTimer();
      return;
    }

    if (isExpired()) {
      performLogout();
      return;
    }

    if (event?.type === 'mousemove') {
      const previous = lastMousePositionRef.current;
      if (previous.x !== null && previous.y !== null) {
        const deltaX = Math.abs(event.clientX - previous.x);
        const deltaY = Math.abs(event.clientY - previous.y);
        if (deltaX < MIN_MOUSEMOVE_DELTA_PX && deltaY < MIN_MOUSEMOVE_DELTA_PX) {
          return;
        }
      }
      lastMousePositionRef.current = { x: event.clientX, y: event.clientY };
    }

    markActivity();
    resetTimer();
  }, [isExpired, isOnSignInPage, markActivity, performLogout, resetTimer]);

  const checkForTimeout = useCallback((reason = 'unknown') => {
    if (isOnSignInPage()) {
      hasLoggedOutRef.current = false;
      markActivity();
      resetTimer();
      return;
    }
    if (hasLoggedOutRef.current) {
      return;
    }
    const expired = isExpired();
    if (expired) {
      performLogout();
    } else {
      resetTimer();
    }
  }, [isExpired, isOnSignInPage, markActivity, performLogout, resetTimer]);

  useEffect(() => {
    if (!getLastActivity()) {
      markActivity();
    }

    const events = ['mousemove', 'keydown', 'click', 'touchstart'];
    events.forEach((evt) => window.addEventListener(evt, handleActivity));

    const onVisibilityChange = () => {
      if (!document.hidden) {
        checkForTimeout('visibilitychange');
      }
    };
    const onFocus = () => checkForTimeout('focus');
    const onPageShow = () => checkForTimeout('pageshow');
    const onStorage = (event) => {
      if (event.key === ADMIN_LOGOUT_EVENT_KEY && event.newValue) {
        sessionStorage.setItem(ADMIN_LOGOUT_REASON_KEY, INACTIVITY_LOGOUT_REASON);
        performLogout();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('storage', onStorage);
    intervalIdRef.current = window.setInterval(() => {
      checkForTimeout('interval');
    }, TIMEOUT_WATCHDOG_INTERVAL_MS);

    checkForTimeout('init');

    return () => {
      events.forEach((evt) => window.removeEventListener(evt, handleActivity));
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('storage', onStorage);
      if (timerIdRef.current) {
        clearTimeout(timerIdRef.current);
      }
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
    };
  }, [checkForTimeout, getLastActivity, handleActivity, markActivity, performLogout, timeout]);

  return null;
}